/**
 * Cover 4 Match Coverage Rules (perception-based)
 *
 * Cover 4 Match now runs on the perception-based reaction engine rather than pre-baked
 * route tables. The corners (CB1 keying WR1, CB2 keying WR4) play man-match ("MOD") via
 * resolveManMatch in AnimationEngine — they read the receiver's movement, not the route.
 *
 * This definition supplies the on-field ALIGNMENT for all seven defenders. Reaction arrays
 * are intentionally empty: CB1/CB2 are driven by the reaction engine, and the remaining
 * defenders (DB/LB1, DB/LB2, FS, SS, LB) hold their alignment until their perception
 * behaviors are built. The legacy route-based Cover 4 Match is preserved in
 * ./archive/cover4MatchRules.ts (doubles) and ./archive/cover4MatchTripsRules.ts (trips).
 */

import {
  CoverageDefinition,
  CoverageReaction,
  Position,
  DBLBReaction,
  FSReaction,
  CB2Reaction,
  DBLB2Reaction,
  SSReaction,
  LBReaction,
} from '../types';

/**
 * Alignment positions for all defenders.
 * Coordinate system: x = lateral (0=left, 100=right), y = downfield (100=LOS, 0=endzone).
 */
const CB1_COVER4MATCH_START: Position = { x: 20, y: 65 };
const DBLB1_COVER4MATCH_START: Position = { x: 30, y: 72 };
const FS_COVER4MATCH_START: Position = { x: 35, y: 45 };
const CB2_COVER4MATCH_START: Position = { x: 80, y: 65 };
const DBLB2_COVER4MATCH_START: Position = { x: 70, y: 72 };
const SS_COVER4MATCH_START: Position = { x: 65, y: 45 };
const LB_COVER4MATCH_START: Position = { x: 50, y: 70 };

// CB1/CB2 are driven by the perception engine; the rest hold — so reaction arrays are empty.
const cover4MatchCB1Reactions: CoverageReaction[] = [];
const cover4MatchDBLB1Reactions: DBLBReaction[] = [];
const cover4MatchFSReactions: FSReaction[] = [];
const cover4MatchCB2Reactions: CB2Reaction[] = [];
const cover4MatchDBLB2Reactions: DBLB2Reaction[] = [];
const cover4MatchSSReactions: SSReaction[] = [];
const cover4MatchLBReactions: LBReaction[] = [];

/**
 * Cover 4 Match definition (perception-based; alignment only).
 */
export const cover4MatchDefinition: CoverageDefinition = {
  id: 'cover4Match',
  name: 'Cover 4 Match',
  description: 'Quarters match — corners play man-match (MOD) via the perception engine.',
  initialPosition: CB1_COVER4MATCH_START,
  reactionRules: cover4MatchCB1Reactions,
  dblb1InitialPosition: DBLB1_COVER4MATCH_START,
  dblb1ReactionRules: cover4MatchDBLB1Reactions,
  fsInitialPosition: FS_COVER4MATCH_START,
  fsReactionRules: cover4MatchFSReactions,
  cb2InitialPosition: CB2_COVER4MATCH_START,
  cb2ReactionRules: cover4MatchCB2Reactions,
  dblb2InitialPosition: DBLB2_COVER4MATCH_START,
  dblb2ReactionRules: cover4MatchDBLB2Reactions,
  ssInitialPosition: SS_COVER4MATCH_START,
  ssReactionRules: cover4MatchSSReactions,
  lbInitialPosition: LB_COVER4MATCH_START,
  lbReactionRules: cover4MatchLBReactions,
};

export function getCover4MatchStartPosition(): Position {
  return { ...CB1_COVER4MATCH_START };
}

export function getCover4MatchDBLB1StartPosition(): Position {
  return { ...DBLB1_COVER4MATCH_START };
}

export function getCover4MatchFSStartPosition(): Position {
  return { ...FS_COVER4MATCH_START };
}

export function getCover4MatchDefinition(): CoverageDefinition {
  return { ...cover4MatchDefinition };
}

export {
  CB1_COVER4MATCH_START,
  DBLB1_COVER4MATCH_START,
  FS_COVER4MATCH_START,
  CB2_COVER4MATCH_START,
  DBLB2_COVER4MATCH_START,
  SS_COVER4MATCH_START,
  LB_COVER4MATCH_START,
  cover4MatchCB1Reactions,
  cover4MatchDBLB1Reactions,
  cover4MatchFSReactions,
  cover4MatchCB2Reactions,
  cover4MatchDBLB2Reactions,
  cover4MatchSSReactions,
  cover4MatchLBReactions,
};
